/**
 * What the tool knows about unit symbols.
 *
 * This is the single source for both jobs that read units: dimensional
 * checking here in the engine, and prefix-aware number formatting in the UI.
 * Two tables would drift, and a formatter that believes `mV` is millivolts
 * while the checker believes it is metre-volts is worse than either alone.
 */

import { dim, dimEqual, formatDimension, type Dimension } from './dimension.ts';

/** A unit symbol resolved to its dimension and its magnitude in SI base units. */
export interface Unit {
  dimension: Dimension;
  /** Multiply a value in this unit by `scale` to get SI base units. */
  scale: number;
}

/**
 * Prefixes accepted when *reading* a unit. Wider than the set used when
 * writing one: `cm` and `daN` are units people genuinely type, but nobody wants
 * to be shown a result in centivolts, so c/d/da/h never come back out.
 *
 * Ordered longest-first so `daN` cannot resolve as deci-aN.
 */
export const PARSE_PREFIXES: [string, number][] = [
  ['da', 1e1],
  ['T', 1e12],
  ['G', 1e9],
  ['M', 1e6],
  ['k', 1e3],
  ['h', 1e2],
  ['d', 1e-1],
  ['c', 1e-2],
  ['m', 1e-3],
  ['µ', 1e-6], // U+00B5 micro sign
  ['μ', 1e-6], // U+03BC greek mu — visually identical, different codepoint
  ['u', 1e-6], // ASCII fallback, what people actually type
  ['n', 1e-9],
  ['p', 1e-12],
  ['f', 1e-15],
];

const L = dim(1);
const MASS = dim(0, 1);
const TIME = dim(0, 0, 1);
const CURRENT = dim(0, 0, 0, 1);
const TEMP = dim(0, 0, 0, 0, 1);
const FORCE = dim(1, 1, -2);
const ENERGY = dim(2, 1, -2);
const POWER = dim(2, 1, -3);
const CHARGE = dim(0, 0, 1, 1);
const VOLTAGE = dim(2, 1, -3, -1);

/**
 * Unit symbols that may carry an SI prefix.
 *
 * Deliberately excludes anything whose prefixed form would collide with a
 * common non-SI unit: `t` (tonne) is out because `ft` would read as
 * femtotonnes, `in` because `min` would read as milli-inches, and `B` because
 * `dB` would read as deci-bels — and a decibel is already logarithmic, so
 * prefixing it is nonsense.
 *
 * Also excluded, deliberately: `%` and `ppm`. They are dimensionless ratios
 * whose scale is ambiguous in a tool that does no conversion — is a value of 5
 * labelled `%` the number 5 or the number 0.05? Leaving them unrecognised means
 * expressions using them are simply not checked, which is the honest answer.
 */
export const BASE_UNITS: Record<string, Unit> = {
  // SI base
  m: { dimension: L, scale: 1 },
  g: { dimension: MASS, scale: 1e-3 }, // the SI base is the kilogram
  s: { dimension: TIME, scale: 1 },
  A: { dimension: CURRENT, scale: 1 },
  K: { dimension: TEMP, scale: 1 },
  mol: { dimension: dim(0, 0, 0, 0, 0, 1), scale: 1 },
  cd: { dimension: dim(0, 0, 0, 0, 0, 0, 1), scale: 1 },

  // Dimensionless SI derived
  rad: { dimension: dim(), scale: 1 },
  sr: { dimension: dim(), scale: 1 },

  // SI derived
  Hz: { dimension: dim(0, 0, -1), scale: 1 },
  N: { dimension: FORCE, scale: 1 },
  Pa: { dimension: dim(-1, 1, -2), scale: 1 },
  J: { dimension: ENERGY, scale: 1 },
  W: { dimension: POWER, scale: 1 },
  C: { dimension: CHARGE, scale: 1 },
  V: { dimension: VOLTAGE, scale: 1 },
  F: { dimension: dim(-2, -1, 4, 2), scale: 1 },
  S: { dimension: dim(-2, -1, 3, 2), scale: 1 },
  Wb: { dimension: dim(2, 1, -2, -1), scale: 1 },
  T: { dimension: dim(0, 1, -2, -1), scale: 1 }, // tesla
  H: { dimension: dim(2, 1, -2, -2), scale: 1 },
  lm: { dimension: dim(0, 0, 0, 0, 0, 0, 1), scale: 1 },
  lx: { dimension: dim(-2, 0, 0, 0, 0, 0, 1), scale: 1 },
  Bq: { dimension: dim(0, 0, -1), scale: 1 },
  Gy: { dimension: dim(2, 0, -2), scale: 1 },
  Sv: { dimension: dim(2, 0, -2), scale: 1 },
  kat: { dimension: dim(0, 0, -1, 0, 0, 1), scale: 1 },
  Ω: { dimension: dim(2, 1, -3, -2), scale: 1 },

  // Accepted non-SI and common engineering compounds
  L: { dimension: dim(3), scale: 1e-3 },
  l: { dimension: dim(3), scale: 1e-3 },
  eV: { dimension: ENERGY, scale: 1.602176634e-19 },
  bar: { dimension: dim(-1, 1, -2), scale: 1e5 },
  Nm: { dimension: ENERGY, scale: 1 },
  Ah: { dimension: CHARGE, scale: 3600 },
  VA: { dimension: POWER, scale: 1 },
  var: { dimension: POWER, scale: 1 },

  /*
   * Degrees Celsius is dimensionally a temperature and is treated as one.
   *
   * The offset is deliberately not modelled. Every use of temperature in a
   * transfer function that this tool is for is a *difference* — `tcr * (T - 25)`
   * — where the offset cancels and °C and K behave identically. Modelling the
   * affine part would require distinguishing "a temperature" from "a
   * temperature interval" in the expression language, which is a large
   * complication for a case that does not arise here.
   */
  '°C': { dimension: TEMP, scale: 1 },
};

/**
 * Alternative spellings, mapped to the canonical symbol.
 *
 * Ω is not on most keyboards, so `ohm` gets typed constantly — and once it is,
 * every capitalisation of it follows. These are not merely accepted: they
 * resolve to the same entry, so a model mixing `Ω` and `ohm` is one unit, and
 * both display as `Ω`.
 *
 * Aliases are listed spelling by spelling rather than matched case-insensitively
 * on purpose. Unit symbols are case-sensitive and must stay that way — `m` and
 * `M`, `s` and `S`, `T` and `t` are all different things, and a
 * case-insensitive lookup would quietly equate them.
 */
export const UNIT_ALIASES: Record<string, string> = {
  ohm: 'Ω',
  ohms: 'Ω',
  Ohm: 'Ω',
  Ohms: 'Ω',
  OHM: 'Ω',
  Omega: 'Ω',
  degC: '°C',
  degK: 'K',
  sec: 's',
};

export interface ResolvedSymbol {
  /** The prefix-free canonical symbol, for display: `Ω` even when `ohm` was typed. */
  canonical: string;
  dimension: Dimension;
  /**
   * Magnitude in **SI base units**, prefix included — `mV` is 1e-3, `g` is
   * 1e-3 because the SI base of mass is the kilogram. This is what dimensional
   * checking compares.
   */
  scale: number;
  /**
   * Magnitude relative to **`canonical`**, prefix only — `mV` is 1e-3, `g` is 1.
   *
   * Distinct from `scale` and the difference bites exactly once, on mass: `kg`
   * has an SI scale of 1 but is 1000 of its own canonical symbol `g`. A
   * formatter using `scale` renders 2.5 kg as `2.500 g`.
   */
  prefixScale: number;
}

function lookup(symbol: string): { canonical: string; unit: Unit } | null {
  const canonical = UNIT_ALIASES[symbol] ?? symbol;
  const unit = Object.prototype.hasOwnProperty.call(BASE_UNITS, canonical)
    ? BASE_UNITS[canonical]
    : undefined;
  return unit ? { canonical, unit } : null;
}

/**
 * Resolve one unit symbol, with or without a prefix.
 *
 * The whole string is tried before any prefix is stripped, which is the
 * difference between `Pa` meaning pascals and meaning pico-annums, and between
 * `mol` meaning moles and milli-ol. Every caller goes through here so the
 * prefix rules cannot drift between the checker and the formatter.
 */
export function resolveSymbol(symbol: string): ResolvedSymbol | null {
  const whole = lookup(symbol);
  if (whole) {
    return {
      canonical: whole.canonical,
      dimension: whole.unit.dimension,
      scale: whole.unit.scale,
      prefixScale: 1,
    };
  }

  for (const [prefix, prefixScale] of PARSE_PREFIXES) {
    if (symbol.length <= prefix.length || !symbol.startsWith(prefix)) continue;
    const base = lookup(symbol.slice(prefix.length));
    if (base) {
      return {
        canonical: base.canonical,
        dimension: base.unit.dimension,
        scale: base.unit.scale * prefixScale,
        prefixScale,
      };
    }
  }

  return null;
}

/** Symbols that name a dimension exactly, preferred when describing one. */
const PREFERRED_NAMES: [string, Dimension][] = [
  ['V', VOLTAGE],
  ['W', POWER],
  ['Ω', dim(2, 1, -3, -2)],
  ['A', CURRENT],
  ['F', BASE_UNITS.F.dimension],
  ['H', BASE_UNITS.H.dimension],
  ['J', ENERGY],
  ['N', FORCE],
  ['Pa', BASE_UNITS.Pa.dimension],
  ['C', CHARGE],
  ['Hz', BASE_UNITS.Hz.dimension],
  ['m', L],
  ['kg', MASS],
  ['s', TIME],
  ['K', TEMP],
];

/**
 * Name a dimension the way an engineer would say it: `V` rather than
 * `kg·m²·s⁻³·A⁻¹`, falling back to base symbols when nothing named fits.
 */
export function describeDimension(d: Dimension): string {
  for (const [name, candidate] of PREFERRED_NAMES) {
    if (dimEqual(d, candidate)) return name;
  }
  return formatDimension(d);
}
